// Payload delle metriche per `POST …/metrics`. Specchio di
// `CloseYourIt::Events::{SlowMethodEvent,PerformanceIssueEvent}` (gem) e
// `MetricEvent`/`PerformanceIssueEvent` (dart). A differenza degli eventi
// errore, `sample_id` è un uuid CON trattini. Le metriche non sono mai
// campionate.

import { isSensitiveKey, FILTERED } from './scrubber'
import type { EventPayload } from './types'
import { uuidV4 } from './uuid'
import { SDK_NAME, SDK_VERSION } from './version'

const SDK_INFO = { name: SDK_NAME, version: SDK_VERSION } as const
const MAX_ARG_VALUE = 120

export interface SlowMethodInput {
  /// Etichetta della misura: è il fingerprint della metrica (obbligatoria).
  label: string
  durationMs: number
  environment: string
  traceId?: string | undefined
  file?: string | undefined
  lineno?: number | undefined
  /// Argomenti opzionali (già gateati da `captureMethodArguments` nel client).
  args?: Record<string, unknown> | undefined
}

/// Payload `kind: slow_method`. Label vuota → TypeError: senza fingerprint la
/// metrica non è raggruppabile (parità dart 0.2.0).
export function buildSlowMethodEvent(input: SlowMethodInput): EventPayload {
  if (typeof input.label !== 'string' || input.label.trim().length === 0) {
    throw new TypeError(
      'closeyourit: measure(label) richiede una label non vuota (è il fingerprint della metrica)',
    )
  }

  return {
    kind: 'slow_method',
    sample_id: uuidV4(),
    duration_ms: round2(input.durationMs),
    occurred_at: new Date().toISOString(),
    environment: input.environment,
    label: input.label,
    ...(input.file ? { file: input.file } : {}),
    ...(input.lineno !== undefined ? { lineno: input.lineno } : {}),
    ...(input.args && Object.keys(input.args).length > 0
      ? { arguments: formatArgs(input.args) }
      : {}),
    ...(input.traceId ? { trace_id: input.traceId } : {}),
    sdk: SDK_INFO,
  }
}

export type PerformanceIssueSubtype = 'slow_external_http' | 'repeated_http' | 'jank'

export interface PerformanceIssueInput {
  subtype: PerformanceIssueSubtype
  environment: string
  durationMs?: number | undefined
  traceId?: string | undefined
  /// slow_external_http / repeated_http
  httpHost?: string | undefined
  httpUrl?: string | undefined
  /// jank
  route?: string | undefined
  /// repeated_http: numero di richieste identiche nella finestra. Sul wire è
  /// `query_count` — stesso nome cross-SDK (dart, gem) e leggibile dal reader
  /// Rails `occurrence_query_count` in UI.
  queryCount?: number | undefined
}

/// Payload `kind: performance_issue`. I campi non pertinenti al subtype
/// restano assenti (il backend raggruppa per signature kind-specifica).
export function buildPerformanceIssueEvent(input: PerformanceIssueInput): EventPayload {
  return {
    kind: 'performance_issue',
    subtype: input.subtype,
    sample_id: uuidV4(),
    ...(input.durationMs !== undefined
      ? { duration_ms: round2(input.durationMs) }
      : {}),
    occurred_at: new Date().toISOString(),
    environment: input.environment,
    ...(input.httpHost ? { http_host: input.httpHost } : {}),
    ...(input.httpUrl ? { http_url: input.httpUrl } : {}),
    ...(input.route ? { route: input.route } : {}),
    ...(input.queryCount !== undefined ? { query_count: input.queryCount } : {}),
    ...(input.traceId ? { trace_id: input.traceId } : {}),
    sdk: SDK_INFO,
  }
}

// `arguments` wire: array di {name, value} con valore stringificato, scrubato
// per nome e troncato (parità slow_method_event.rb).
function formatArgs(
  args: Record<string, unknown>,
): Array<{ name: string; value: string }> {
  return Object.entries(args).map(([name, value]) => ({
    name,
    value: isSensitiveKey(name) ? FILTERED : truncate(stringify(value)),
  }))
}

function stringify(value: unknown): string {
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value) ?? String(value)
  } catch {
    return String(value)
  }
}

function truncate(value: string): string {
  return value.length > MAX_ARG_VALUE ? `${value.slice(0, MAX_ARG_VALUE)}…` : value
}

function round2(value: number): number {
  return Math.round(value * 100) / 100
}
